/**
 * Sur quels jours un événement d'agenda doit-il s'afficher ?
 *
 * Un rendez-vous de plusieurs jours — une pose de couverture du mardi au
 * jeudi, un congé d'une semaine — n'existait dans l'agenda du CRM que le jour
 * de son `debut_at` : rangé sous cette seule clé, il disparaissait dès le
 * lendemain. Sur les agendas Google importés, plus de cinq cents événements
 * sont dans ce cas.
 *
 * Tout est raisonné en heure LOCALE : c'est la journée qu'un couvreur lit sur
 * son écran qui compte, pas la journée UTC.
 */

/** Garde-fou : un événement plus long qu'un an est une saisie aberrante, pas
 *  une raison de produire une clé par jour jusqu'à épuisement de la mémoire. */
export const JOURS_COUVERTS_MAX = 366;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** La clé d'une journée locale, `AAAA-MM-JJ`. */
export function cleJour(d: Date): string {
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function jourSuivant(d: Date): Date {
  // Par les composantes, et non par « +24 h » : aux changements d'heure une
  // journée fait 23 ou 25 heures, et l'addition en millisecondes dérive.
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
}

/**
 * Les journées locales qu'un événement occupe, de la première à la dernière.
 *
 * Une fin à minuit pile appartient à la veille : un rendez-vous de 22 h à
 * minuit ne déborde pas sur le jour suivant. Une fin antérieure au début
 * (donnée incohérente) est ramenée au seul jour du début.
 */
export function joursCouverts(debutIso: string, finIso: string): string[] {
  const debut = new Date(debutIso);
  const fin = new Date(finIso);
  if (Number.isNaN(debut.getTime())) return [];
  const premier = new Date(debut.getFullYear(), debut.getMonth(), debut.getDate());
  if (Number.isNaN(fin.getTime()) || fin <= debut) return [cleJour(premier)];

  let dernier = new Date(fin.getFullYear(), fin.getMonth(), fin.getDate());
  if (fin.getHours() === 0 && fin.getMinutes() === 0 && fin.getSeconds() === 0) {
    dernier = new Date(dernier.getFullYear(), dernier.getMonth(), dernier.getDate() - 1);
  }
  if (dernier < premier) dernier = premier;

  const jours: string[] = [];
  for (let d = premier; d <= dernier && jours.length < JOURS_COUVERTS_MAX; d = jourSuivant(d)) {
    jours.push(cleJour(d));
  }
  return jours;
}

/** Vrai si l'événement ne fait que se poursuivre ce jour-là : il a commencé avant. */
export function estSuite(debutIso: string, finIso: string, jour: string): boolean {
  return joursCouverts(debutIso, finIso)[0] !== jour;
}

/**
 * Ce que l'événement occupe SUR CE JOUR-LÀ.
 *
 * Un chantier de mardi à jeudi ne commence pas à 8 h le mercredi : il court
 * déjà. Afficher « 08:00 – 17:00 » les trois jours ferait croire à trois
 * rendez-vous distincts.
 */
export function plageDuJour(
  e: { debut_at: string; fin_at: string; journee_entiere: boolean },
  jour: string,
  heure: (iso: string) => string,
): string {
  if (e.journee_entiere) return 'Journée';
  const jours = joursCouverts(e.debut_at, e.fin_at);
  const premier = jours[0] === jour;
  const dernier = jours[jours.length - 1] === jour;
  if (premier && dernier) return `${heure(e.debut_at)} – ${heure(e.fin_at)}`;
  if (premier) return `${heure(e.debut_at)} → …`;
  if (dernier) return `… → ${heure(e.fin_at)}`;
  return 'Journée';
}
